<template>
  <div class="min-h-screen">
    <!-- Processing Overlay -->
    <v-overlay :value="!!processingReport" z-index="9999" opacity="0.7">
      <div class="flex flex-col items-center justify-center text-center">
        <v-progress-circular
          indeterminate
          size="64"
          width="6"
          color="primary"
          class="mb-4"
        ></v-progress-circular>
        <h3
          class="text-white font-semibold text-lg bg-black/60 px-6 py-3 rounded-lg backdrop-blur-sm shadow-xl border border-white/20"
        >
          Generating {{ processingReport }}...
        </h3>
      </div>
    </v-overlay>

    <ActitiveTabcomponent :tabs="reportTabs" :defaultTab="activeTab">
      <!-- Loan Management Section Tab -->
      <template #loan_management>
        <div class="p-2">
          <div class="mb-2" v-if="sectionVisible('loan_management')">
            <!-- <h2 class="text-lg font-medium text-gray-900 mb-2 font-semibold">Loan Management</h2> -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <v-card
                flat
                class="cursor-pointer hover:bg-gray-50 border border-gray-200 rounded-lg transition-all duration-200"
                @click="openReportDialog('Loan Collection Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-50 p-3 rounded-lg border border-blue-100">
                      <v-icon color="blue darken-4" size="24">mdi-cash-multiple</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800 mb-2 text-base">Loan Collection Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed font-medium">
                        Track and analyze total loan collections and performance trends across
                        regions.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                flat
                class="cursor-pointer hover:bg-gray-50 border border-gray-200 rounded-lg transition-all duration-200"
                @click="openReportDialog('Daily Collection Status Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-50 p-3 rounded-lg border border-blue-100">
                      <v-icon color="blue darken-4" size="24">mdi-calendar-check</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800 mb-2 text-base">
                        Daily Collection Status
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed font-medium">
                        Monitor day-by-day collection status and agent productivity in real-time.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('EMI DUE Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-clock-alert</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">EMI DUE Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        View upcoming and pending EMI payments for immediate follow-up.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Detailed Due Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-document-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Detailed Due Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Comprehensive summary of all pending dues with detailed aging analysis.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Non Starter Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-account-plus-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Non Starter Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Track accounts where the first installment has not yet been collected.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Loan Expiry Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-alert-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Loan Expiry Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Identify loans that have passed their tenure but still have pending dues.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('VAS Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-currency-usd-off</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">VAS Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Report on Value Added Services charges including bounce and late fees.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Days Past Due Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-calendar-alert</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Days Past Due Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Track accounts by their DPD status, including cumulative overdue and source
                        details.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Overdue List Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-clipboard-list-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Overdue List Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Complete list of overdue accounts with pending EMI dates, counts, and
                        amounts.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('TransUnion Consumer Data Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-export-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        TransUnion CIBIL Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Weekly submission file containing decrypted customer and open/active trade
                        line details.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Crif HighMark Consumer Data Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-export-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Crif HighMark Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Weekly submission file containing decrypted customer and open/active trade
                        line details in CRIF format.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Equifax Consumer Data Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-export-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Equifax Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Weekly submission file containing decrypted customer and open/active trade
                        line details in Equifax format.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Experian Consumer Data Report', 'loan_management')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-export-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Experian Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Weekly submission file containing decrypted customer and open/active trade
                        line details in Experian format.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </template>

      <!-- Financial Reports Section Tab -->
      <template #financial>
        <div class="p-2">
          <div class="mb-6" v-if="sectionVisible('loan_management')">
            <!-- <h2 class="text-lg font-medium text-gray-900 mb-2 font-semibold">Financial Reports</h2> -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('LCC Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-indigo-100 p-3 rounded-lg">
                      <v-icon color="indigo darken-2" size="24">mdi-file-chart</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">LCC Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Comprehensive landscape of loan lifecycle including demand, collection, and
                        VAS.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Loan PAR Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-indigo-100 p-3 rounded-lg">
                      <v-icon color="indigo darken-2" size="24">mdi-finance</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Loan PAR Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Analyze Portfolio at Risk based on various delinquency buckets.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('EMI Tracker Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-teal-100 p-3 rounded-lg">
                      <v-icon color="teal darken-2" size="24">mdi-cash-sync</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">EMI Tracker Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        End-to-end tracking of all installments from generation to payment.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Cash Inflow Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-green-100 p-3 rounded-lg">
                      <v-icon color="green darken-2" size="24">mdi-bank-transfer-in</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Cash Inflow Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Summary of all recoveries including principal, interest, and charges.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Cash Outflow Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-red-100 p-3 rounded-lg">
                      <v-icon color="red darken-2" size="24">mdi-bank-transfer-out</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Cash Outflow Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Track disbursed amounts, processing fees, and net outflow.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Business Done Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-orange-100 p-3 rounded-lg">
                      <v-icon color="orange darken-2" size="24">mdi-briefcase-check</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Business Done Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Total monthly business sanctioned and overall growth trends.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Loan Closure Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-grey-100 p-3 rounded-lg">
                      <v-icon color="blue-grey darken-2" size="24">mdi-check-decagram</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Loan Closure Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Track fully repaid accounts and finalized loan agreements.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Trial Balance Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-amber-100 p-3 rounded-lg">
                      <v-icon color="amber darken-3" size="24">mdi-scale-balance</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Trial Balance Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Summary of all ledger balances grouped by categories for period
                        verification.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Trial Balance (Opening & Closing) Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-indigo-100 p-3 rounded-lg">
                      <v-icon color="indigo darken-3" size="24">mdi-scale-unbalanced</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Trial Balance (Opening & Closing) Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Tally-style trial balance showing detailed opening and closing debit/credit columns for all ledger groups.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Profit and Loss Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-emerald-100 p-3 rounded-lg">
                      <v-icon color="emerald darken-3" size="24">mdi-finance</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Profit and Loss Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Comprehensive P&L statement combining loan revenues and accounting ledgers.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Balance Sheet Report', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-light-blue-100 p-3 rounded-lg">
                      <v-icon color="light-blue darken-3" size="24">mdi-bank-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Balance Sheet Report
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Snapshot of company's financial position including Assets, Liabilities, and
                        Equity.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Day Book', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-indigo-100 p-3 rounded-lg">
                      <v-icon color="indigo darken-3" size="24">mdi-book-open-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Day Book</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        View and download all double entry journals recorded on a single day.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Bank Book', 'financial')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-teal-100 p-3 rounded-lg">
                      <v-icon color="teal darken-3" size="24">mdi-bank-transfer</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Bank Book</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Summary and closing balances of all bank/cash accounts on a selected day.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </template>

      <!-- Workforce & Attendance Reports Section Tab -->
      <template #workforce>
        <div class="p-2">
          <div class="mb-6" v-if="sectionVisible('attendance_management')">
            <!-- <h2 class="text-lg font-medium text-gray-900 mb-2 font-semibold">
              Workforce & Attendance
            </h2> -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Attendance Report', 'workforce')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-calendar-check</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Attendance Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Detailed attendance matrix with clock-in/out times and locations.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Muster Report-I', 'workforce')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-table-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">
                        Muster Report-I (DEPRECATED)
                      </h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Legacy formatted attendance report with all employee details.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Muster Report-II', 'workforce')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-blue-100 p-3 rounded-lg">
                      <v-icon color="blue darken-2" size="24">mdi-file-chart-outline</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-semibold text-gray-900 mb-2 text-base">Muster Report-II</h3>
                      <p class="text-sm text-gray-600 leading-relaxed">
                        Time-series based attendance and activity summary report.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </template>

      <!-- Collection Management Section Tab -->
      <template #collection>
        <div class="p-2">
          <div class="mb-6" v-if="sectionVisible('collection_management')">
            <!-- <h2 class="text-lg font-medium text-gray-900 mb-2 font-semibold">
              Collection Management
            </h2> -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Collection Report', 'collection')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-orange-50 p-3 rounded-lg border border-orange-100">
                      <v-icon color="orange darken-4" size="24">mdi-folder-account</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800 mb-2 text-base">Collection Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed font-medium">
                        Overview of all collection cases with status and assignment details.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                elevation="0"
                @click="openReportDialog('Visit Report', 'collection')"
              >
                <v-card-text class="p-6">
                  <div class="flex items-start space-x-4">
                    <div class="bg-orange-50 p-3 rounded-lg border border-orange-100">
                      <v-icon color="orange darken-4" size="24">mdi-map-marker-distance</v-icon>
                    </div>
                    <div class="flex-1">
                      <h3 class="font-bold text-gray-800 mb-2 text-base">Visit Report</h3>
                      <p class="text-sm text-gray-600 leading-relaxed font-medium">
                        Detailed log of all field visits, outcomes and timestamps.
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </template>
    </ActitiveTabcomponent>
    <!-- showPreviewButton -->
    <GenericExportColumns
      ref="exportDialog"
      :showDialog.sync="showExportDialog"
      :exportKeys="currentExportKeys"
      :categories="currentExportCategories"
      :dialogTitle="'Export ' + selectedReport.title"
      :identifier="selectedReport.title"
      :lockSelection="
        [
          'Trial Balance Report',
          'Trial Balance (Opening & Closing) Report',
          'Profit and Loss Report',
          'Balance Sheet Report',
          'Vertical Balance Sheet Report'
        ].includes(selectedReport.title) || (selectedReport.title && selectedReport.title.toLowerCase().includes('trial balance'))
      "
      :previewRows="previewRows"
      :previewHeaders="previewHeaders"
      :previewHtml="previewHtml"
      :loadingPreview="!!processingReport"
      :allowPdf="allowPdfDownload"
      @exportData="handleExportData"
      @previewData="handlePreviewData"
      @closeExportDialog="showExportDialog = false"
    >
      <template #additional-filters>
        <!-- filters same as before -->
        <div class="px-2 py-4">
          <v-row dense>
            <!-- Date Filter -->
            <!-- Date Filter -->
            <v-col cols="12" md="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="slide-y-transition"
                offset-y
                min-width="auto"
                nudge-bottom="8"
              >
                <template #activator="{ attrs }">
                  <v-text-field
                    v-bind="attrs"
                    @click="dateMenu = true"
                    :value="
                      isSingleDateReport
                        ? dates[dates.length - 1]
                          ? $dayjs(dates[dates.length - 1]).format('DD MMM YYYY')
                          : ''
                        : dateRangeText
                    "
                    :label="dateFieldLabel"
                    readonly
                    outlined
                    dense
                    clearable
                    @click:clear.stop.prevent="
                      dates = []
                      dateError = ''
                      dateMenu = false
                    "
                    :error-messages="dateError"
                    :hide-details="!dateError"
                    prepend-inner-icon="mdi-calendar"
                    class="bg-gray-50/50 rounded-lg cursor-pointer"
                    color="primary"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="pickerDate"
                  :range="!isSingleDateReport"
                  :max="maxDatePickerDate"
                  no-title
                  color="primary"
                  @input="onDateInput"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <!-- Loan Closure Date Filter (CIBIL Reports only) -->
            <v-col cols="12" md="4" v-if="isCibilReport">
              <v-menu
                v-model="closureDateMenu"
                :close-on-content-click="false"
                transition="slide-y-transition"
                offset-y
                min-width="auto"
                nudge-bottom="8"
              >
                <template #activator="{ attrs }">
                  <v-text-field
                    v-bind="attrs"
                    @click="closureDateMenu = true"
                    :value="closureDateRangeText ? $dayjs(closureDates[0]).format('DD MMM YYYY') + (closureDates[1] ? ' to ' + $dayjs(closureDates[1]).format('DD MMM YYYY') : '') : ''"
                    label="Closure Date Range"
                    readonly
                    outlined
                    dense
                    clearable
                    @click:clear.stop.prevent="
                      closureDates = []
                      closureDateMenu = false
                    "
                    hide-details
                    prepend-inner-icon="mdi-calendar-check"
                    class="bg-gray-50/50 rounded-lg cursor-pointer"
                    color="primary"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="closurePickerDate"
                  range
                  no-title
                  color="primary"
                  @input="onClosureDateInput"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <!-- BS Format -->
            <v-col
              cols="12"
              md="4"
              v-if="
                selectedReport.title === 'Balance Sheet Report' ||
                selectedReport.title === 'Vertical Balance Sheet Report'
              "
            >
              <v-select
                v-model="balanceSheetFormat"
                :items="[
                  { text: 'Horizontal (Standard)', value: 'horizontal' },
                  { text: 'Vertical (Tree)', value: 'vertical' }
                ]"
                label="Layout Format"
                dense
                outlined
                hide-details
                color="primary"
                class="rounded-lg bg-gray-50/50"
              ></v-select>
            </v-col>

            <!-- TB Format -->
            <v-col
              cols="12"
              md="4"
              v-if="
                selectedReport.title &&
                selectedReport.title.toLowerCase().includes('trial balance')
              "
            >
              <v-select
                v-model="trialBalanceFormat"
                :items="[
                  { text: 'Standard (Net Balances)', value: 'standard' },
                  { text: 'Opening & Closing (Tally Style)', value: 'opening_closing' }
                ]"
                label="Layout Format"
                dense
                outlined
                hide-details
                color="primary"
                class="rounded-lg bg-gray-50/50"
              ></v-select>
            </v-col>

            <!-- Assignee -->
            <v-col cols="12" md="4" v-if="showLoanFilters">
              <v-autocomplete
                v-model="collectionFilter.assignedTo"
                :items="allUsers"
                item-text="fullName"
                item-value="_id"
                label="Select Assignee"
                placeholder="All Assignees"
                outlined
                dense
                hide-details
                clearable
                class="bg-gray-50/50 rounded-lg"
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mr-1">mdi-account-search</v-icon>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- SubCase -->
            <v-col
              cols="12"
              md="4"
              v-if="['collection', 'loan_management'].includes(selectedReport.type)"
            >
              <v-select
                v-model="collectionFilter.subCase"
                :items="filteredSubCases"
                item-text="name"
                item-value="_id"
                label="Select Sub Case"
                placeholder="All Sub Cases"
                outlined
                dense
                hide-details
                clearable
                class="bg-gray-50/50 rounded-lg"
              >
                <template v-slot:prepend-inner>
                  <v-icon x-small class="mr-1">mdi-filter-variant</v-icon>
                </template>
              </v-select>
            </v-col>

            <!-- Branch -->
            <v-col cols="12" md="4" v-if="selectedReport.type">
              <v-autocomplete
                v-model="collectionFilter.branch"
                :items="branches"
                item-text="branchName"
                item-value="_id"
                label="Select Branch"
                placeholder="All Branches"
                outlined
                dense
                hide-details
                clearable
                class="bg-gray-50/50 rounded-lg"
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mr-1 mt-1">mdi-map-marker</v-icon>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- Loan Search -->
            <v-col cols="12" md="4" v-if="showLoanFilters">
              <v-text-field
                v-model="collectionFilter.loanSearch"
                label="Search Loan/App No"
                placeholder="Loan/App No"
                outlined
                dense
                hide-details
                clearable
                class="bg-gray-50/50 rounded-lg"
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mr-1 mt-1">mdi-magnify</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <!-- Visit Status -->
            <v-col cols="12" md="4" v-if="selectedReport.title === 'Visit Report'">
              <v-select
                v-model="collectionFilter.status"
                :items="['PENDING', 'IN_PROGRESS', 'COMPLETED']"
                label="Visit Status"
                placeholder="All Statuses"
                outlined
                dense
                hide-details
                clearable
                class="bg-gray-50/50 rounded-lg"
              >
                <template v-slot:prepend-inner>
                  <v-icon x-small class="mr-1">mdi-check-circle-outline</v-icon>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </div>
      </template>

      <!-- Integrated Preview Slot -->
      <template #preview-content="{ rows, headers, html }">
        <div
          class="report-preview-container bg-white rounded-lg border border-gray-100 p-2 overflow-auto"
          style="min-height: 500px"
        >
          <template v-if="isBalanceSheetPreview">
            <BalanceSheetHorizontalTemplate
              v-if="selectedReport.title === 'Balance Sheet Report'"
              :data="rows"
              :startDate="[...dates].sort()[0]"
              :endDate="[...dates].sort()[[...dates].length - 1]"
              @account-click="handleAccountLink"
            />
            <BalanceSheetVerticalTemplate
              v-else-if="selectedReport.title === 'Vertical Balance Sheet Report'"
              :data="rows"
              :startDate="[...dates].sort()[0]"
              :endDate="[...dates].sort()[[...dates].length - 1]"
              @account-click="handleAccountLink"
            />
            <TrialBalanceOpeningClosingTemplate
              v-else-if="
                selectedReport.title === 'Trial Balance (Opening & Closing) Report' ||
                (selectedReport.title.toLowerCase().includes('trial balance') && trialBalanceFormat === 'opening_closing')
              "
              :data="rows"
              :startDate="[...dates].sort()[0]"
              :endDate="[...dates].sort()[[...dates].length - 1]"
              @account-click="handleAccountLink"
            />
            <TrialBalanceTemplate
              v-else-if="selectedReport.title.toLowerCase().includes('trial balance')"
              :data="rows"
              :startDate="[...dates].sort()[0]"
              :endDate="[...dates].sort()[[...dates].length - 1]"
              @account-click="handleAccountLink"
            />
            <ProfitLossTemplate
              v-else-if="selectedReport.title.toLowerCase().includes('profit and loss')"
              :data="rows"
              :startDate="[...dates].sort()[0]"
              :endDate="[...dates].sort()[[...dates].length - 1]"
              @account-click="handleAccountLink"
            />
          </template>
          <div v-else-if="html" v-html="html" @click="handlePreviewAccountClick"></div>
          <v-data-table
            v-else-if="rows && rows.length"
            :headers="headers"
            :items="rows"
            flat
            dense
            fixed-header
            height="500px"
            class="elevation-0"
            hide-default-footer
            disable-pagination
            disable-sort
          >
            <template v-slot:[`item.${loanNoPreviewKey}`]="{ item }">
              <a @click="openLoanDetail(item)" class="blue--text cursor-pointer font-weight-bold">
                {{ item[loanNoPreviewKey] }}
              </a>
            </template>
          </v-data-table>
          <div v-else class="flex flex-col items-center justify-center py-20 text-gray-400">
            <template v-if="hasFetchedPreview">
              <v-icon size="64" color="gray lighten-3">mdi-database-off-outline</v-icon>
              <p class="mt-4 text-lg">No data available for the selected filters</p>
            </template>
            <template v-else>
              <v-icon size="64" color="gray lighten-3">mdi-file-eye-outline</v-icon>
              <p class="mt-4 text-lg">Report preview will appear here</p>
            </template>
          </div>
        </div>
      </template>
    </GenericExportColumns>

    <!-- (Preview Dialog Removed - Integrated into GenericExportColumns) -->

    <!-- Hidden Container for Balance Sheet PDF Export -->
    <div v-if="isExportingPdf" style="position: absolute; left: -9999px; top: -9999px">
      <div ref="bsPdfExportContainer">
        <BalanceSheetHorizontalTemplate
          v-if="selectedReport.title === 'Balance Sheet Report'"
          :data="previewRows"
          :startDate="dates[0]"
          :endDate="dates[1]"
        />
        <BalanceSheetVerticalTemplate
          v-else-if="selectedReport.title === 'Vertical Balance Sheet Report'"
          :data="previewRows"
          :startDate="dates[0]"
          :endDate="dates[1]"
        />
        <TrialBalanceOpeningClosingTemplate
          v-else-if="
            selectedReport.title === 'Trial Balance (Opening & Closing) Report' ||
            (selectedReport.title.toLowerCase().includes('trial balance') && trialBalanceFormat === 'opening_closing')
          "
          :data="previewRows"
          :startDate="dates[0]"
          :endDate="dates[1]"
        />
        <TrialBalanceTemplate
          v-else-if="selectedReport.title.toLowerCase().includes('trial balance')"
          :data="previewRows"
          :startDate="dates[0]"
          :endDate="dates[1]"
        />
        <ProfitLossTemplate
          v-else-if="selectedReport.title.toLowerCase().includes('profit and loss')"
          :data="previewRows"
          :startDate="dates[0]"
          :endDate="dates[1]"
        />
      </div>
    </div>

    <!-- Case Details Dialog -->
    <v-dialog v-model="openCaseDetails" max-width="98rem" persistent>
      <CaseDetails
        v-if="openCaseDetails && selectedCaseDetails && selectedCaseConfig"
        :selectedCase="selectedCaseDetails"
        :caseConfig="selectedCaseConfig"
        @closeDialog="closeCaseDetailsDialog"
      />
    </v-dialog>

    <!-- Loader overlay for case details fetching -->
    <v-overlay :value="loadingCaseDetails" z-index="99999">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import { hasIamPermission } from '@/utils/iamEvaluator'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'
import GenericExportColumns from '@/components/common/GenericExportColumns.vue'
import dayjs from 'dayjs'
import { collectionService } from '@/services/collection'
import { axiosUrl } from '@/utils/workforceApi'
import { downloadExcelJs, isModuleVisible, downloadPdf } from '@/utils/common'
import CaseDetails from '@/components/Workforce/CollectionManagement/CaseDetails.vue'
import { REPORT_COLUMNS } from '@/utils/reportConfig'

import { isModuleFeatureAllowed, sortDropdownOptions } from '@/utils/common'
import ExcelJS from 'exceljs'
import {
  ATTENDANCE_COLUMN_MAPPERS,
  processAttendanceItems,
  ATTENDANCE_VALID_COLS
} from '@/utils/workforce/attendanceReportUtils'
import html2pdf from 'html2pdf.js'

const REPORT_DATE_CONFIGS = {
  // Single date reports (range = false)
  'Daily Collection Status Report': { type: 'single', label: 'Select Date' },
  'EMI DUE Report': { type: 'single', label: 'As on Date (Last Receipt)' },
  'Detailed Due Report': { type: 'single', label: 'As on Date' },
  'Loan Expiry Report': { type: 'single', label: 'As on Date' },
  'Days Past Due Report': { type: 'single', label: 'As on Date' },
  'LCC Report': { type: 'single', label: 'As on Date' },
  'Loan PAR Report': { type: 'single', label: 'As on Date' },
  'Trial Balance Report': { type: 'accounting-as-on', label: 'As on Date' },
  'Trial Balance (Opening & Closing) Report': { type: 'range', label: 'Select Date Range' },
  'Balance Sheet Report': { type: 'accounting-as-on', label: 'As on Date' },
  'Vertical Balance Sheet Report': { type: 'accounting-as-on', label: 'As on Date' },
  'Day Book': { type: 'range', maxDays: 365, label: 'Select Date Range (Max 1 year)' },
  'Bank Book': { type: 'single', label: 'Select Date' },
  'TransUnion Consumer Data Report': { type: 'single', label: 'As on Date' },
  'Crif HighMark Consumer Data Report': { type: 'single', label: 'As on Date' },
  'Equifax Consumer Data Report': { type: 'single', label: 'As on Date' },
  'Experian Consumer Data Report': { type: 'single', label: 'As on Date' },

  // Date range reports with limit (range = true)
  'Cash Inflow Report': { type: 'range', maxDays: 90, label: 'Select Date Range (Max 90 days)' },
  'Cash Outflow Report': { type: 'range', maxDays: 90, label: 'Select Date Range (Max 90 days)' },
  'Overdue List Report': { type: 'single', label: 'As on Date' },
  'Loan Collection Report': {
    type: 'range',
    maxDays: 30,
    label: 'Select Date Range (Max 30 days)'
  },
  'Collection Report': { type: 'range', maxDays: 30, label: 'Select Date Range (Max 30 days)' },
  'Business Done Report': { type: 'range', maxDays: 365, label: 'Select Date Range (Max 1 year)' },
  'Profit and Loss Report': {
    type: 'range',
    maxDays: 365,
    label: 'Select Date Range (Max 1 year)'
  },

  // Date range reports with no limit
  'Non Starter Report': { type: 'range', label: 'Select Date Range' },
  'VAS Report': { type: 'range', label: 'Select Date Range' },
  'EMI Tracker Report': { type: 'range', label: 'Select Date Range' },
  'Accounts Journals Report': { type: 'range', label: 'Select Date Range' }
}

export default {
  name: 'ReportsDashboard',
  data() {
    return {
      showExportDialog: false,
      showPreviewDialog: false,
      previewRows: [],
      previewHeaders: [],
      previewHtml: '',
      isExportingPdf: false,
      hasFetchedPreview: false,
      balanceSheetFormat: 'horizontal',
      trialBalanceFormat: 'standard',
      dateError: '',
      selectedReport: {
        title: '',
        type: ''
      },
      currentExportKeys: [],
      currentExportCategories: [],
      currentSelectedColumns: [], // Added to track columns during preview
      processingReport: null, // Added processingReport
      openCaseDetails: false,
      selectedCaseDetails: null,
      selectedCaseConfig: null,
      loadingCaseDetails: false,
      reportTabs: [
        {
          key: 'loan_management',
          label: 'Loan Management',
          icon: 'mdi-cash-multiple',
          component: null,
          permissionKey: 'reports.loan_management.read'
        },
        {
          key: 'financial',
          label: 'Financial Reports',
          icon: 'mdi-finance',
          component: null,
          permissionKey: 'reports.financial.read'
        },
        {
          key: 'workforce',
          label: 'Workforce & Attendance',
          icon: 'mdi-calendar-check',
          component: null,
          permissionKey: 'reports.workforce.read'
        },
        {
          key: 'collection',
          label: 'Collection Management',
          icon: 'mdi-folder-account',
          component: null,
          permissionKey: 'reports.collection.read'
        }
      ],
      activeTab: 'loan_management',
      showFilterDialog: false,
      dates: [],
      dateMenu: false,
      closureDates: [],
      closureDateMenu: false,
      attendanceSettings: null,
      allUsers: [],
      allSubCases: [],
      collectionFilter: {
        assignedTo: null,
        subCase: null,
        status: null,
        branch: null,
        loanSearch: null
      },
      branches: [],
      teams: []
    }
  },
  watch: {
    balanceSheetFormat(newVal) {
      if (
        this.selectedReport.title === 'Balance Sheet Report' ||
        this.selectedReport.title === 'Vertical Balance Sheet Report'
      ) {
        const actualTitle =
          newVal === 'vertical' ? 'Vertical Balance Sheet Report' : 'Balance Sheet Report'
        this.selectedReport.title = actualTitle
        this.currentExportKeys = REPORT_COLUMNS[actualTitle]?.columns || []
      }
    },
    trialBalanceFormat(newVal) {
      if (
        this.selectedReport.title &&
        this.selectedReport.title.toLowerCase().includes('trial balance')
      ) {
        const actualTitle =
          newVal === 'opening_closing'
            ? 'Trial Balance (Opening & Closing) Report'
            : 'Trial Balance Report'
        this.selectedReport.title = actualTitle
        this.currentExportKeys = REPORT_COLUMNS[actualTitle]?.columns || []
      }
    },
    'collectionFilter.subCase': {
      handler() {
        this.updateReportHeaders()
      }
    },
    dates: {
      handler() {
        // Do not auto-refresh; user must click Preview or Export explicitly
      }
    }
  },
  computed: {
    iamStatements() {
      return this.$store.state.wfmStatements || []
    },
    isBalanceSheetPreview() {
      if (!this.selectedReport?.title) return false
      const title = this.selectedReport.title
      return (
        [
          'Balance Sheet Report',
          'Vertical Balance Sheet Report',
          'Trial Balance Report',
          'Trial Balance (Opening & Closing) Report'
        ].includes(title) ||
        title.toLowerCase().includes('trial balance') ||
        title.toLowerCase().includes('profit and loss')
      )
    },
    maxDatePickerDate() {
      return dayjs().format('YYYY-MM-DD')
    },
    isAsOnDateReport() {
      return [
        'Balance Sheet Report',
        'Vertical Balance Sheet Report',
        'Trial Balance Report'
      ].includes(this.selectedReport.title)
    },
    currentReportConfig() {
      if (!this.selectedReport?.title) return { type: 'range', label: 'Select Date Range' }
      return (
        REPORT_DATE_CONFIGS[this.selectedReport.title] || {
          type: 'range',
          label: 'Select Date Range'
        }
      )
    },
    isSingleDateReport() {
      const config = this.currentReportConfig
      return config.type === 'single' || config.type === 'accounting-as-on'
    },
    dateFieldLabel() {
      return this.currentReportConfig.label || 'Select Date Range'
    },
    showLoanFilters() {
      const loanTypes = ['collection', 'loan_management']
      const excludeTitles = [
        'Profit and Loss Report',
        'Trial Balance Report',
        'Trial Balance (Opening & Closing) Report',
        'Balance Sheet Report',
        'Vertical Balance Sheet Report'
      ]

      if (loanTypes.includes(this.selectedReport.type)) return true
      if (
        this.selectedReport.type === 'financial' &&
        !excludeTitles.includes(this.selectedReport.title)
      ) {
        return true
      }
      return false
    },
    allowPdfDownload() {
      if (!this.selectedReport?.title) return false
      const title = this.selectedReport.title.toLowerCase()
      return (
        title.includes('trial balance') ||
        title.includes('profit and loss') ||
        title.includes('balance sheet')
      )
    },
    loanNoPreviewKey() {
      if (!this.previewHeaders || !this.previewHeaders.length) return null
      const h = this.previewHeaders.find(
        (h) =>
          h.value &&
          (h.value.startsWith('loan_account_no') ||
            h.value.startsWith('case_no') ||
            h.value.startsWith('loan_no') ||
            h.value.startsWith('loan_account_number') ||
            h.value.startsWith('applicationNo'))
      )
      return h ? h.value : null
    },
    filteredSubCases() {
      const reportType = this.selectedReport?.type
      return this.allSubCases.filter((s) => {
        if (s.subCaseStatus !== 'ACTIVE') return false
        if (reportType) {
          return s.useCase === reportType
        }
        return true
      })
    },
    dateRangeText() {
      const dates = Array.isArray(this.dates) ? this.dates : [this.dates]
      if (!dates || !dates[0]) return ''
      if (dates.length === 1 || !dates[1]) return dates[0]
      const sorted = [...dates].sort()
      return `${sorted[0]} to ${sorted[1]}`
    },
    pickerDate: {
      get() {
        if (this.isSingleDateReport) {
          return Array.isArray(this.dates) ? this.dates[this.dates.length - 1] : this.dates
        }
        return Array.isArray(this.dates) ? this.dates : []
      },
      set(val) {
        const config = this.currentReportConfig
        if (config.type === 'accounting-as-on') {
          if (val) {
            const date = dayjs(val)
            const year = date.month() < 3 ? date.year() - 1 : date.year()
            const startDate = `${year}-04-01`
            this.dates = [startDate, val]
          } else {
            this.dates = []
          }
        } else if (config.type === 'single') {
          this.dates = val ? [val, val] : []
        } else {
          this.dates = val || []
        }
      }
    },
    closureDateRangeText() {
      const dates = Array.isArray(this.closureDates) ? this.closureDates : [this.closureDates]
      if (!dates || !dates[0]) return ''
      if (dates.length === 1 || !dates[1]) return dates[0]
      const sorted = [...dates].sort()
      return `${sorted[0]} to ${sorted[1]}`
    },
    closurePickerDate: {
      get() {
        return Array.isArray(this.closureDates) ? this.closureDates : []
      },
      set(val) {
        this.closureDates = val || []
      }
    },
    isCibilReport() {
      const title = this.selectedReport?.title
      return [
        'TransUnion Consumer Data Report',
        'Crif HighMark Consumer Data Report',
        'Equifax Consumer Data Report',
        'Experian Consumer Data Report'
      ].includes(title)
    }
  },
  beforeDestroy() {},

  components: {
    ActitiveTabcomponent,
    GenericExportColumns,
    CaseDetails,
    BalanceSheetHorizontalTemplate: () => import('./Accounting/BalanceSheetHorizontalTemplate.vue'),
    BalanceSheetVerticalTemplate: () => import('./Accounting/BalanceSheetVerticalTemplate.vue'),
    TrialBalanceTemplate: () => import('./Accounting/TrialBalanceTemplate.vue'),
    TrialBalanceOpeningClosingTemplate: () => import('./Accounting/TrialBalanceOpeningClosingTemplate.vue'),
    ProfitLossTemplate: () => import('./Accounting/ProfitLossTemplate.vue')
  },

  methods: {
    sectionVisible(section) {
      return isModuleVisible(section)
    },
    openReportDialog(title, type) {
      this.selectedReport = { title, type }
      if (title === 'Balance Sheet Report') {
        this.balanceSheetFormat = 'horizontal'
      }
      if (title.includes('Trial Balance')) {
        this.trialBalanceFormat =
          title.includes('Opening') || title.includes('Closing')
            ? 'opening_closing'
            : 'standard'
      }
      this.currentExportKeys = REPORT_COLUMNS[title]?.columns || []
      this.currentExportCategories = REPORT_COLUMNS[title]?.categories || []
      this.showExportDialog = true
      this.hasFetchedPreview = false
      this.dates = [] // Reset dates
      this.closureDates = [] // Reset closure dates
      this.closureDateMenu = false
      this.dateError = '' // Reset error state
      this.collectionFilter = {
        assignedTo: null,
        subCase: null,
        status: null,
        branch: null,
        loanSearch: null
      } // Reset filters

      if (['collection', 'loan_management', 'financial'].includes(type)) {
        this.fetchFilterData()
      }
    },
    async fetchFilterData() {
      try {
        if (!this.allUsers.length) {
          const usersResp = await this.$axios.get('/v3/users', { params: { limit: 1000 } })
          this.allUsers = usersResp.data?.users || usersResp.data?.data || []
        }
        if (!this.allSubCases.length) {
          const subcaseResp = await collectionService.getExtendedUsecases()
          this.allSubCases = subcaseResp.data?.data || []
        }
        if (!this.branches.length) {
          const branchResp = await this.$axios.get(axiosUrl.branches.getBranches)
          this.branches = branchResp.data?.branches || []
        }
        if (!this.teams?.length) {
          const teamResp = await this.$axios.get(axiosUrl.teams.getTeams)
          this.teams = teamResp.data?.teams || []
        }

        if (
          ['collection', 'loan_management'].includes(this.selectedReport.type) &&
          !this.collectionFilter.subCase
        ) {
          if (this.filteredSubCases.length > 0) {
            this.collectionFilter.subCase = this.filteredSubCases[0]._id
          }
        }
        this.updateReportHeaders()
      } catch (err) {
        console.error('Failed to fetch filter data', err)
      }
    },
    updateReportHeaders() {
      if (!this.collectionFilter.subCase || !this.allSubCases.length) return

      const subCase = this.allSubCases.find((s) => s._id === this.collectionFilter.subCase)
      if (!subCase) return

      const fm = {
        ...subCase.baseFieldMappings,
        ...subCase.collectionManagementFieldMapping,
        ...subCase.loanManagementFieldMapping,
        ...subCase.customerFieldMappings
      }

      const getText = (field, defaultText) => field?.displayName || defaultText

      const caseTitle = subCase.tableTabMapping?.caseTable?.displayName || 'Case'

      if (this.selectedReport.title === 'Collection Report') {
        this.currentExportKeys = [
          { text: getText(fm.caseNo, `${caseTitle} No`), value: 'case_no' },
          { text: getText(fm.crn, 'CRN'), value: 'crn' },
          { text: getText(fm.customerFullName, 'Customer Name'), value: 'customer_name' },
          { text: getText(fm.customerMobile, 'Customer Mobile'), value: 'customer_mobile' },
          { text: getText(fm.typeOfLoan, `${caseTitle} Type`), value: 'loan_type' },
          { text: getText(fm.principalOutstanding, 'POS'), value: 'outstanding_principal' },
          { text: getText(fm.emiAmount, 'EMI Amount'), value: 'emi_amount_due' },
          { text: getText(fm.bucket, 'Bucket'), value: 'bucket' },
          { text: getText(fm.tenure, 'Tenure'), value: 'tenure' },
          { text: getText(fm.productInfo, 'Product Info'), value: 'product_info' },
          { text: getText(fm.assignedTo, 'Assigned To'), value: 'agent_name' },
          { text: 'Agent ID', value: 'agent_id' },
          { text: getText(fm.currentStatus, 'Status'), value: 'emi_status' },
          { text: getText(fm.caseOutcomeStatus, 'Outcome'), value: 'outcome' },
          { text: 'Start Date', value: 'case_start_date' },
          { text: 'Closed Date', value: 'case_end_date' },
          { text: 'TAT', value: 'tat' }
        ]

        if (subCase.additionalColumns) {
          Object.values(subCase.additionalColumns).forEach((col) => {
            if (col && col.key) {
              this.currentExportKeys.push({
                text: col.displayName || col.key,
                value: `additional_${col.key}`
              })
            }
          })
        }
      } else if (this.selectedReport.title === 'Visit Report') {
        const visitTitle = subCase.tableTabMapping?.visitTable?.displayName || 'Visit'
        this.currentExportKeys = [
          { text: `${visitTitle} Title`, value: 'visit_title' },
          { text: getText(fm.caseNo, 'Case No'), value: 'case_no' },
          { text: getText(fm.customerFullName, 'Customer Name'), value: 'customer_name' },
          { text: `${visitTitle} Status`, value: 'visit_status' },
          { text: 'Started At', value: 'visit_started' },
          { text: 'Completed At', value: 'visit_completed' },
          { text: getText(fm.assignedTo, 'Assigned To'), value: 'visit_assigned_to' },
          { text: 'Meeting Outcome', value: 'meeting_outcome' }
        ]
      }
    },
    async handleExportData(selectedColumns, format = 'excel') {
      await this.handleReportAction('export', selectedColumns, format)
    },
    async handlePreviewData(selectedColumns) {
      this.currentSelectedColumns = selectedColumns
      if (this.$refs.exportDialog) {
        this.$refs.exportDialog.viewMode = 'preview'
      }
      await this.handleReportAction('preview', selectedColumns)
    },
    onDateInput() {
      const config = this.currentReportConfig
      this.dateError = ''
      if (this.isSingleDateReport) {
        this.dateMenu = false
      } else if (this.dates.length === 2) {
        const sorted = [...this.dates].sort()
        const start = dayjs(sorted[0])
        const end = dayjs(sorted[1])
        const diffDays = Math.abs(end.diff(start, 'day'))
        if (config.type === 'range' && config.maxDays && diffDays > config.maxDays) {
          const errMsg = `Date range cannot exceed ${config.maxDays} days for this report`
          this.$toast?.error(errMsg)
          this.dateError = errMsg
          this.dates = []
        } else {
          this.dateMenu = false
        }
      }
    },
    onClosureDateInput() {
      if (this.closureDates.length === 2) {
        this.closureDateMenu = false
      }
    },
    onSearchInput() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.refreshPreview()
      }, 600)
    },
    refreshPreview() {
      // Use nextTick to ensure v-model values (like cleared inputs) are updated before fetching
      this.$nextTick(() => {
        this.handleReportAction('preview', this.currentSelectedColumns)
      })
    },
    async openLoanDetail(item) {
      if (!item) return
      const subCaseId =
        this.collectionFilter.subCase ||
        item.subCaseId ||
        item.subCase ||
        item.loan?.subCaseId ||
        (this.filteredSubCases.length ? this.filteredSubCases[0]._id : null)

      if (!subCaseId) {
        this.$toast?.error('Sub Case not identified')
        return
      }

      if (!this.allSubCases || !this.allSubCases.length) {
        try {
          const subcaseResp = await collectionService.getExtendedUsecases()
          this.allSubCases = subcaseResp.data?.data || []
        } catch (err) {
          console.error('Failed to fetch subcases in openLoanDetail', err)
        }
      }

      const subCase = subCaseId ? this.allSubCases.find((s) => s._id === subCaseId) : null
      if (!subCase) {
        this.$toast?.error('Sub Case configuration not found')
        return
      }

      let caseData = null
      if (item.caseNo && (item.customer || item.loanDetails)) {
        caseData = item
      } else if (item.loan && item.loan.caseNo && (item.loan.customer || item.loan.loanDetails)) {
        caseData = item.loan
      }

      if (caseData) {
        const fieldMappings = {
          ...subCase.baseFieldMappings,
          ...subCase.collectionManagementFieldMapping,
          ...subCase.loanManagementFieldMapping,
          ...subCase.customerFieldMappings
        }
        this.selectedCaseConfig = {
          ...subCase,
          fieldMappings
        }
        this.selectedCaseDetails = caseData
        this.openCaseDetails = true
      } else {
        this.$toast?.error('Case data not fully loaded in report preview')
      }
    },
    closeCaseDetailsDialog() {
      this.openCaseDetails = false
      this.selectedCaseDetails = null
      this.selectedCaseConfig = null
    },
    async handleReportAction(actionType, selectedColumns = [], exportFormat = 'excel') {
      const reportTitle = this.selectedReport.title
      this.processingReport = reportTitle

      const filters = {}
      if (this.dates && this.dates.length === 2) {
        const sorted = [...this.dates].sort()
        filters.start = sorted[0]
        filters.end = sorted[1]
      } else if (this.dates && this.dates.length === 1) {
        filters.start = this.dates[0]
        filters.end = this.dates[0]
      }
      const config = this.currentReportConfig
      this.dateError = ''

      if (
        config.type === 'accounting-as-on' ||
        config.type === 'single' ||
        config.maxDays ||
        this.selectedReport.type === 'attendance'
      ) {
        if (!this.dates || !this.dates.length) {
          const errMsg = this.isSingleDateReport
            ? 'Please select a date first'
            : 'Please select a date range first'
          this.$toast?.error(errMsg)
          this.dateError = errMsg
          this.processingReport = null
          return
        }
      }

      // Validate maxDays constraint
      if (config.type === 'range' && config.maxDays && this.dates && this.dates.length === 2) {
        const start = dayjs(this.dates[0])
        const end = dayjs(this.dates[1])
        const diffDays = Math.abs(end.diff(start, 'day'))
        if (diffDays > config.maxDays) {
          const errMsg = `Date range cannot exceed ${config.maxDays} days for this report`
          this.$toast?.error(errMsg)
          this.dateError = errMsg
          this.processingReport = null
          return
        }
      }

      if (actionType === 'preview' && this.isBalanceSheetPreview) {
        try {
          const [ledgerGroupsRes, journalsRes] = await Promise.all([
            collectionService.getLedgerGroups(),
            reportTitle === 'Profit and Loss Report'
              ? collectionService.getAggregatedJournals({ start: filters.start, end: filters.end })
              : collectionService.getAggregatedBsJournals({ start: filters.start, end: filters.end })
          ])

          const ledgerGroups = ledgerGroupsRes.data?.ledgerGroups || []
          const journals = journalsRes.data?.journals || []

          const reportColConfig = REPORT_COLUMNS[reportTitle]
          if (!reportColConfig || typeof reportColConfig.getRows !== 'function') {
            throw new Error(`No row mapper found for ${reportTitle}`)
          }

          const rows = reportColConfig.getRows({ journals, ledgerGroups }, filters)

          this.previewRows = rows
          this.previewHeaders = reportColConfig.columns || []
          this.hasFetchedPreview = true
        } catch (err) {
          console.error('Failed to fetch preview data:', err)
          this.$toast?.error('Failed to fetch preview data: ' + (err?.message || 'Unknown error'))
          this.previewRows = []
          this.previewHeaders = []
          this.hasFetchedPreview = false
        } finally {
          this.processingReport = null
        }
        return
      }

      try {
        const payloadFilters = {
          start: filters.start,
          end: filters.end,
          branch: this.collectionFilter.branch,
          search: this.collectionFilter.loanSearch,
          status: this.collectionFilter.status,
          subCase: this.collectionFilter.subCase,
          assignedTo: this.collectionFilter.assignedTo,
          selectedColumns: selectedColumns
        }

        if (this.isCibilReport && this.closureDates && this.closureDates.length > 0) {
          const sorted = [...this.closureDates].sort()
          payloadFilters.closureStart = sorted[0]
          payloadFilters.closureEnd = sorted[1] || sorted[0]
        }

        await this.$axios.post('/orgSetup/batch-report', {
          reportType: reportTitle,
          filters: payloadFilters
        })
        this.showExportDialog = false
        this.$toast?.success(
          'Report queued! Generating your report — check the Batch Reports page.'
        )
        this.$router.push('/workforce/batch-reports')
      } catch (err) {
        console.error('Failed to queue batch report:', err)
        this.$toast?.error('Failed to request batch report: ' + (err?.message || 'Unknown error'))
      } finally {
        this.processingReport = null
      }
    },
    async downloadFinancialExcel(rows, title, type) {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet(title)

      const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFADD8E6' } }
      const profitFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00FFCC' } }
      const border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      }

      if (type === 'TB_OPENING_CLOSING' || title.includes('Opening and Closing') || title.includes('Opening & Closing')) {
        const org = this.$store?.state?.user?.organization || this.$store?.state?.orgConfig || this.$storage?.getUniversal('organization') || {}
        const orgName = org.organization_name || org.name || org.companyName || 'Organization'

        let dateStr = ''
        if (this.dates && this.dates.length === 2) {
          const sorted = [...this.dates].sort()
          dateStr = `${dayjs(sorted[0]).format('D-MMM-YY')} to ${dayjs(sorted[1]).format('D-MMM-YY')}`
        } else if (this.dates && this.dates.length === 1) {
          const endD = dayjs(this.dates[0])
          const m = endD.month()
          const fy = m < 3 ? endD.year() - 1 : endD.year()
          dateStr = `1-Apr-${String(fy).slice(-2)} to ${endD.format('D-MMM-YY')}`
        } else {
          const now = dayjs()
          const m = now.month()
          const fy = m < 3 ? now.year() - 1 : now.year()
          dateStr = `1-Apr-${String(fy).slice(-2)} to ${now.format('D-MMM-YY')}`
        }

        worksheet.getColumn(1).width = 45
        worksheet.getColumn(2).width = 20
        worksheet.getColumn(3).width = 20
        worksheet.getColumn(4).width = 20
        worksheet.getColumn(5).width = 20

        const topHeaderFill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFB8D1E8' }
        }

        // Row 1: Top Bar
        worksheet.mergeCells('A1:B1')
        worksheet.mergeCells('C1:E1')
        const cellA1 = worksheet.getCell('A1')
        cellA1.value = 'Trial Balance'
        cellA1.font = { bold: true, size: 11 }
        cellA1.alignment = { horizontal: 'left', vertical: 'middle' }

        const cellC1 = worksheet.getCell('C1')
        cellC1.value = orgName
        cellC1.font = { bold: true, size: 11 }
        cellC1.alignment = { horizontal: 'center', vertical: 'middle' }

        ;['A1', 'B1', 'C1', 'D1', 'E1'].forEach((pos) => {
          worksheet.getCell(pos).fill = topHeaderFill
          worksheet.getCell(pos).border = border
        })

        // Row 2: Particulars & Merged Header
        worksheet.mergeCells('A2:A3')
        worksheet.mergeCells('B2:E2')

        const cellA2 = worksheet.getCell('A2')
        cellA2.value = 'P a r t i c u l a r s'
        cellA2.font = { bold: true, size: 11 }
        cellA2.alignment = { horizontal: 'left', vertical: 'middle' }

        const cellB2 = worksheet.getCell('B2')
        cellB2.value = `${orgName}\n${dateStr}`
        cellB2.font = { bold: true, size: 10 }
        cellB2.alignment = {
          horizontal: 'center',
          vertical: 'middle',
          wrapText: true
        }

        ;['A2', 'B2', 'C2', 'D2', 'E2', 'A3'].forEach((pos) => {
          worksheet.getCell(pos).border = border
        })

        // Row 3: Opening & Closing Balance
        worksheet.mergeCells('B3:C3')
        worksheet.mergeCells('D3:E3')

        const cellB3 = worksheet.getCell('B3')
        cellB3.value = 'Opening Balance'
        cellB3.font = { bold: true, size: 10 }
        cellB3.alignment = { horizontal: 'center', vertical: 'middle' }

        const cellD3 = worksheet.getCell('D3')
        cellD3.value = 'Closing Balance'
        cellD3.font = { bold: true, size: 10 }
        cellD3.alignment = { horizontal: 'center', vertical: 'middle' }

        ;['B3', 'C3', 'D3', 'E3'].forEach((pos) => {
          worksheet.getCell(pos).border = border
        })

        // Row 4: Column Headers
        const row4 = worksheet.addRow(['', 'Debit', 'Credit', 'Debit', 'Credit'])
        row4.font = { bold: true, size: 10 }
        row4.alignment = { horizontal: 'right', vertical: 'middle' }
        row4.getCell(1).alignment = { horizontal: 'left' }
        row4.eachCell((cell) => {
          cell.border = border
        })

        const formatAmt = (val) =>
          val === undefined || val === null || isNaN(Number(val))
            ? ''
            : Number(val).toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })

        let grandOpDebit = 0
        let grandOpCredit = 0
        let grandClDebit = 0
        let grandClCredit = 0

        rows.forEach((g) => {
          grandOpDebit += g.openingDebit || 0
          grandOpCredit += g.openingCredit || 0
          grandClDebit += g.closingDebit || 0
          grandClCredit += g.closingCredit || 0

          const gRow = worksheet.addRow([
            g.name,
            g.openingDebit ? formatAmt(g.openingDebit) : '',
            g.openingCredit ? formatAmt(g.openingCredit) : '',
            g.closingDebit ? formatAmt(g.closingDebit) : '',
            g.closingCredit ? formatAmt(g.closingCredit) : ''
          ])
          gRow.font = { bold: true, size: 10 }
          gRow.eachCell((cell, colNumber) => {
            cell.border = border
            if (colNumber > 1) {
              cell.alignment = { horizontal: 'right' }
            }
          })

          if (g.items && g.items.length > 0) {
            g.items.forEach((it) => {
              const itRow = worksheet.addRow([
                `    ${it.name}`,
                it.openingDebit ? formatAmt(it.openingDebit) : '',
                it.openingCredit ? formatAmt(it.openingCredit) : '',
                it.closingDebit ? formatAmt(it.closingDebit) : '',
                it.closingCredit ? formatAmt(it.closingCredit) : ''
              ])
              itRow.font = { size: 9, color: { argb: 'FF444444' } }
              itRow.eachCell((cell, colNumber) => {
                cell.border = border
                if (colNumber > 1) {
                  cell.alignment = { horizontal: 'right' }
                }
              })
            })
          }
        })

        // Grand Total Row
        const grandTotalRow = worksheet.addRow([
          'G r a n d   T o t a l',
          formatAmt(grandOpDebit),
          formatAmt(grandOpCredit),
          formatAmt(grandClDebit),
          formatAmt(grandClCredit)
        ])
        grandTotalRow.font = { bold: true, size: 10 }
        grandTotalRow.eachCell((cell, colNumber) => {
          cell.border = border
          if (colNumber > 1) {
            cell.alignment = { horizontal: 'right' }
          }
        })
      } else {
        // Set column widths manually
        worksheet.getColumn(1).width = 50
        worksheet.getColumn(2).width = 25

        // NO COLUMN HEADERS (Particulars/Amount) - Starting directly with Title
        const titleRow = worksheet.addRow([title])
        titleRow.font = { size: 18, bold: true, color: { argb: 'FF2D76A8' } }
        worksheet.mergeCells(`A${titleRow.number}:B${titleRow.number}`)

        const dateStr = this.dates[0]
          ? dayjs(this.dates[0]).format('DD-MM-YYYY')
          : dayjs().format('DD-MM-YYYY')
        const subtitleRow = worksheet.addRow(['', `Balance Sheet for the Period ${dateStr}.`])
        subtitleRow.font = { italic: true, size: 11, bold: true }
        subtitleRow.alignment = { horizontal: 'right' }

        worksheet.addRow([]) // Spacer

        let verticalData = []

        if (type === 'BS') {
          const isHorizontal = rows.some((r) => r.le_particulars !== undefined)
          if (isHorizontal) {
            verticalData.push({ text: 'Liabilities', isMainHeader: true })
            rows.forEach((r) => {
              if (r.le_particulars && !r.isGrandTotal) {
                let text = r.le_particulars
                if (text.includes('Liabilities & Equities')) text = 'Liabilities'
                verticalData.push({
                  text: text,
                  amount: r.le_amount,
                  isSubHeader: r.le_isSubHeader,
                  isItem: r.le_isItem,
                  isSectionTotal: r.le_isSectionTotal,
                  isProfit: r.le_isProfit,
                  side: 'L'
                })
              }
            })
            const totalL = rows.find((r) => r.isGrandTotal)?.le_amount || '0.00'
            verticalData.push({ text: 'Total Liabilities', amount: totalL, isGrandTotal: true })
            verticalData.push({ isSpacer: true })

            verticalData.push({ text: 'Assets', isMainHeader: true })
            rows.forEach((r) => {
              if (r.assets_particulars && !r.isGrandTotal) {
                verticalData.push({
                  text: r.assets_particulars,
                  amount: r.assets_amount,
                  isSubHeader: r.as_isSubHeader,
                  isItem: r.as_isItem,
                  isSectionTotal: r.as_isSectionTotal,
                  side: 'A'
                })
              }
            })
            const totalA = rows.find((r) => r.isGrandTotal)?.assets_amount || '0.00'
            verticalData.push({ text: 'Total Assets', amount: totalA, isGrandTotal: true })
          } else {
            const assetsHeaderIdx = rows.findIndex((r) => r.vbs_account === 'Assets')
            const liabilitiesHeaderIdx = rows.findIndex((r) =>
              r.vbs_account?.toLowerCase().includes('liabilities')
            )

            let liabilitiesPart = []
            let assetsPart = []

            if (liabilitiesHeaderIdx !== -1) {
              const endIdx = rows.findIndex((r, i) => i > liabilitiesHeaderIdx && r.isMainTotal)
              liabilitiesPart = rows.slice(liabilitiesHeaderIdx, endIdx + 1)
            }
            if (assetsHeaderIdx !== -1) {
              const endIdx = rows.findIndex((r, i) => i > assetsHeaderIdx && r.isMainTotal)
              assetsPart = rows.slice(assetsHeaderIdx, endIdx + 1)
            }

            const unified = [...liabilitiesPart, { isSpacer: true }, ...assetsPart]
            unified.forEach((r) => {
              if (r.isSpacer) verticalData.push({ isSpacer: true })
              else {
                let text = r.vbs_account
                if (text.includes('Liabilities & Equities')) text = 'Liabilities'
                verticalData.push({
                  text: text,
                  amount: r.vbs_total,
                  isMainHeader: r.isMainHeader,
                  isSubHeader: r.isSubHeader,
                  isItem: r.isItem,
                  isSectionTotal: r.isSectionTotal,
                  isGrandTotal: r.isMainTotal,
                  side: assetsPart.includes(r) || r.vbs_account === 'Assets' ? 'A' : 'L'
                })
              }
            })
          }
        } else if (type === 'TB') {
          rows.forEach((g) => {
            verticalData.push({ text: g.name, isSubHeader: true })
            g.items.forEach((it) => {
              const net = (it.debit || 0) - (it.credit || 0)
              const suffix = net >= 0 ? ' Dr' : ' Cr'
              verticalData.push({
                text: it.name,
                amount: Math.abs(net).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + suffix,
                isItem: true
              })
            })
            const gNet = (g.debit || 0) - (g.credit || 0)
            const gSuffix = gNet >= 0 ? ' Dr' : ' Cr'
            verticalData.push({
              text: `Total ${g.name}`,
              amount: Math.abs(gNet).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + gSuffix,
              isSectionTotal: true
            })
            verticalData.push({ isSpacer: true })
          })
        } else if (type === 'PNL') {
          const formatAmt = (val) =>
            val === undefined || val === ''
              ? ''
              : Number(val).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })

          verticalData.push({ text: 'Income', isMainHeader: true })
          const incItems = rows.rawIncList || []
          incItems.forEach((inc) => {
            if (!inc.isGrossProfit && !inc.isProfit && !inc.isTotalRow) {
              verticalData.push({
                text: inc.particulars,
                amount: inc.amount !== undefined ? formatAmt(inc.amount) : '',
                isSubHeader: inc.isSubHeader,
                isItem: inc.isItem,
                side: 'I'
              })
            }
          })
          const totalInc =
            rows.totalIncome !== undefined
              ? rows.totalIncome
              : incItems.find((i) => i.isTotalRow)?.amount || 0
          verticalData.push({
            text: 'Total Income',
            amount: formatAmt(totalInc),
            isSectionTotal: true,
            side: 'I'
          })
          verticalData.push({ isSpacer: true })

          verticalData.push({ text: 'Expenses', isMainHeader: true })
          const expItems = rows.rawExpList || []
          expItems.forEach((exp) => {
            if (!exp.isGrossProfit && !exp.isProfit && !exp.isTotalRow) {
              verticalData.push({
                text: exp.particulars,
                amount: exp.amount !== undefined ? formatAmt(exp.amount) : '',
                isSubHeader: exp.isSubHeader,
                isItem: exp.isItem,
                side: 'E'
              })
            }
          })
          const totalExp =
            rows.totalExpense !== undefined
              ? rows.totalExpense
              : expItems.find((i) => i.isTotalRow)?.amount || 0
          verticalData.push({
            text: 'Total Expenses',
            amount: formatAmt(totalExp),
            isSectionTotal: true,
            side: 'E'
          })
          verticalData.push({ isSpacer: true })

          const grossProfit = rows.grossProfit !== undefined ? rows.grossProfit : 0
          verticalData.push({
            text: grossProfit >= 0 ? 'Gross Profit' : 'Gross Loss',
            amount: formatAmt(Math.abs(grossProfit)),
            isSectionTotal: true
          })

          const netProfit =
            rows.netProfit !== undefined ? rows.netProfit : totalInc - totalExp
          verticalData.push({
            text: netProfit >= 0 ? 'Net Profit' : 'Net Loss',
            amount: formatAmt(Math.abs(netProfit)),
            isGrandTotal: true,
            isProfit: true
          })
        }

        verticalData.forEach((item) => {
          if (item.isSpacer) {
            worksheet.addRow([])
            return
          }

          let amountStr = item.amount || ''
          if (
            type === 'BS' &&
            item.isItem &&
            amountStr &&
            amountStr !== '0.00' &&
            !amountStr.includes('Dr') &&
            !amountStr.includes('Cr')
          ) {
            amountStr += item.side === 'L' ? ' Cr' : ' Dr'
          }

          const row = worksheet.addRow([item.text, amountStr])
          row.eachCell((cell) => {
            cell.border = border
          })

          if (item.isMainHeader || item.isSubHeader) {
            row.eachCell((cell) => {
              cell.fill = headerFill
              cell.font = { bold: true }
            })
          }
          if (item.isSectionTotal || item.isGrandTotal) {
            row.eachCell((cell) => {
              cell.font = { bold: true }
            })
          }
          if (item.isProfit || item.text === 'Profit') {
            row.eachCell((cell) => {
              cell.fill = profitFill
              cell.font = { bold: true }
            })
          }
          if (item.isItem) {
            row.getCell(1).alignment = { indent: 1 }
          }
        })
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${title.replace(/\s+/g, '_')}.xlsx`
      link.click()
    },

    getReportIcon(title) {
      if (title.includes('Collection')) return 'mdi-cash-register'
      if (title.includes('EMI')) return 'mdi-clock-alert'
      if (title.includes('Detailed')) return 'mdi-file-find'
      if (title.includes('Attendance')) return 'mdi-calendar-check'
      if (title.includes('Muster')) return 'mdi-file-table-outline'
      return 'mdi-file-chart'
    },

    handleAccountLink(accountName) {
      let url = `/workforce/accounts?mainTab=chart_of_accounts&subTab=0&search=${encodeURIComponent(
        accountName
      )}&fromReport=true`
      if (this.dates && this.dates.length > 0) {
        const sorted = [...this.dates].sort()
        const start = sorted[0]
        const end = sorted[sorted.length - 1]
        url += `&start=${start}&end=${end}`
      }
      window.open(url, '_blank')
    },

    handlePreviewAccountClick(event) {
      const target = event.target.closest('[data-account-link]')
      if (!target) return
      const accountName = target.getAttribute('data-account-link')
      if (!accountName) return
      let url = `/workforce/accounts?mainTab=chart_of_accounts&subTab=0&search=${encodeURIComponent(
        accountName
      )}&fromReport=true`
      if (this.dates && this.dates.length > 0) {
        const sorted = [...this.dates].sort()
        const start = sorted[0]
        const end = sorted[sorted.length - 1]
        url += `&start=${start}&end=${end}`
      }
      window.open(url, '_blank')
    },

    openLoanFromPreview(loanNo) {
      if (!loanNo || loanNo === '-') return

      // Get the subCase used when generating this report
      const subCaseId =
        this.collectionFilter.subCase ||
        (this.filteredSubCases.length > 0 ? this.filteredSubCases[0]._id : null)

      const subCase = subCaseId ? this.allSubCases.find((s) => s._id === subCaseId) : null

      if (subCase) {
        const useCase = subCase.useCase || 'collection'
        const url = `/workforce/${useCase}/${subCase._id}?loanSearch=${encodeURIComponent(loanNo)}`
        window.open(url, '_blank')
      } else {
        // Fallback: open reports with the loan no pre-filled in search filter
        window.open(`/workforce/reports?loanSearch=${encodeURIComponent(loanNo)}`, '_blank')
      }
    },

    async handleAttendanceExport(selectedColumns, actionType = 'export') {
      const reportTitle = this.selectedReport.title
      if (!this.dates || this.dates.length < 2) {
        this.$toast.error('Please select a date range first')
        return
      }
      const sortedDates = [...this.dates].sort()
      const start = sortedDates[0]
      const end = sortedDates[1]

      try {
        // 1. Fetch Users efficiently
        this.$toast?.info('Fetching users list...')
        let allUsers = []
        let page = 1
        let hasMore = true
        while (hasMore) {
          const usersResp = await this.$axios.get('/v3/users', {
            params: { limit: 1000, page }
          })
          const fetchedData = usersResp.data?.users || usersResp.data?.data || []
          if (fetchedData.length > 0) {
            allUsers = [...allUsers, ...fetchedData]
            page++
            if (fetchedData.length < 1000) hasMore = false
          } else {
            hasMore = false
          }
        }

        if (allUsers.length === 0) {
          this.$toast?.warning('No users found.')
          return
        }

        if (reportTitle === 'Attendance Report' || reportTitle === 'Muster Report-I') {
          this.$toast?.info('Fetching attendance records. This may take a moment...')

          if (!this.attendanceSettings) {
            try {
              const { data } = await this.$axios.get('/orgSetup/attendance-settings')
              this.attendanceSettings = data.attendanceSettings || {}
            } catch (err) {
              console.error('Failed to fetch attendance settings', err)
            }
          }

          // Fetch Attendance Summary using efficient chunking
          const attendanceData = await this.fetchAttendanceInChunks(allUsers, start, end)

          // Combine Data using Generic Utility
          const items = processAttendanceItems(
            allUsers,
            attendanceData,
            this.attendanceSettings,
            process.env.VUE_APP_S3_BASE_URL
          )

          const currentMonthDates = []
          let curr = dayjs(start)
          const endDay = dayjs(end)
          while (curr.isBefore(endDay) || curr.isSame(endDay, 'day')) {
            currentMonthDates.push(curr.format('YYYY-MM-DD'))
            curr = curr.add(1, 'day')
          }

          if (actionType === 'preview') {
            this.$toast?.info(
              'Preview is currently available for Loan/Financial reports only. Downloading full report instead.'
            )
          }

          if (reportTitle === 'Attendance Report') {
            await this.generateAttendanceMatrix(items, currentMonthDates, selectedColumns)
          } else {
            await this.generateMusterReportI(items, currentMonthDates, selectedColumns)
          }
        } else if (reportTitle === 'Muster Report-II') {
          this.$toast?.info(
            'Fetching granular activity data for all users. This may take a few minutes...'
          )

          // 1. Calculate Max Managers
          const reporteeChain = this.$storage?.getUniversal('userReporteeChain') || {}
          let maxManagers = 0
          allUsers.forEach((u) => {
            const chain = reporteeChain[u.orgEmployeeId || u.employeeId]?.managerChain || ''
            const list = chain ? chain.split(' -> ') : []
            maxManagers = Math.max(maxManagers, list.length)
          })

          // 2. Generate Export Structure (Categorization)
          const structure = { mainHeaders: [], subHeaders: [], keys: [] }
          const categories = [
            {
              title: 'Employee Details',
              columns: ['Employee Name', 'Emp Id', 'Department', 'Designation', 'City']
            },
            { title: 'Manager Details', columns: ['Direct Manager', 'Manager Hierarchy'] },
            {
              title: 'Daily Attendance Reports',
              columns: [
                'Date',
                'Attendance',
                'First Clock-In',
                'Last Clock-Out',
                'Shift Start Time',
                'Planned Working Hour',
                'Completed Working Hour',
                'Breaks Count',
                'Overtime',
                'Delay In Shift',
                'Clock-In Image',
                'Clock-Out Image'
              ]
            },
            {
              title: 'Activity Details',
              columns: [
                'Activity Date',
                'Activity',
                'Activity Start Time',
                'Activity End Time',
                'Duration of Activity',
                'Activity Start Location',
                'Activity End Location'
              ]
            },
            {
              title: 'Visit Details',
              columns: [
                'Visit Date',
                'Customer Name',
                'Arrival Time',
                'Departure Time',
                'Duration of Visit',
                'Visit Location',
                'Daily Total Visits',
                'Distance Breakup Per Day',
                'Total Distance Per Day',
                'Mode of Transportation',
                'Notes/Comments'
              ]
            },
            {
              title: 'Monthly Travel Summary',
              columns: ['Month', 'Total Distance Traveled', 'Total Visits Done']
            }
          ]

          categories.forEach((cat, idx) => {
            const matched = []
            if (cat.title === 'Manager Details') {
              if (selectedColumns.includes('Direct Manager')) matched.push('Direct Manager')
              if (selectedColumns.includes('Manager Hierarchy')) {
                for (let i = 1; i <= maxManagers; i++) matched.push(`Manager Level ${i}`)
                matched.push('Manager Hierarchy')
              }
            } else {
              cat.columns.forEach((col) => {
                if (selectedColumns.includes(col)) matched.push(col)
              })
            }

            if (matched.length > 0) {
              const start = structure.subHeaders.length + 1
              structure.subHeaders.push(...matched)
              structure.keys.push(...matched)
              structure.mainHeaders.push({
                title: cat.title,
                start,
                end: start + matched.length - 1,
                color: idx % 2 === 0 ? 'FFCFE2F3' : 'FFD9EAD3'
              })
            }
          })

          const workbook = new ExcelJS.Workbook()
          const worksheet = workbook.addWorksheet('Muster Report II')

          // Header Row 1: Main Categories
          const mainHeaderRow = worksheet.getRow(1)
          structure.mainHeaders.forEach((mh) => {
            worksheet.mergeCells(1, mh.start, 1, mh.end)
            const cell = mainHeaderRow.getCell(mh.start)
            cell.value = mh.title
            cell.font = { size: 14, bold: true }
            cell.alignment = { horizontal: 'center', vertical: 'middle' }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: mh.color } }
          })
          mainHeaderRow.height = 30

          // Header Row 2: Sub Headers
          const subHeaderRow = worksheet.getRow(2)
          subHeaderRow.values = structure.subHeaders
          subHeaderRow.font = { bold: true, size: 12 }
          subHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' }
          subHeaderRow.height = 25

          // Fetch data concurrently
          const [userTimeSeriesData, attendanceRecords] = await Promise.all([
            this.fetchTimeSeriesConcurrently(allUsers, start, end),
            this.fetchAttendanceInChunks(allUsers, start, end)
          ])

          // Map attendance records: userId -> Date -> status
          const attendanceMap = {}
          attendanceRecords.forEach((att) => {
            const uId = att.userId?._id || att.userId
            const d = dayjs(att.attendanceDate || att.attendanceDateUTC).format('YYYY-MM-DD')
            if (!attendanceMap[uId]) attendanceMap[uId] = {}
            attendanceMap[uId][d] = att.attendanceStatus
          })

          let srNo = 1
          const dates = []
          let curr = dayjs(start)
          while (curr.isBefore(dayjs(end)) || curr.isSame(dayjs(end), 'day')) {
            dates.push(curr.format('YYYY-MM-DD'))
            curr = curr.add(1, 'day')
          }

          for (const user of allUsers) {
            const userPoints = userTimeSeriesData[user._id] || []
            const reporteeChain = this.$storage?.getUniversal('userReporteeChain') || {}
            const chainObj =
              reporteeChain[user.orgEmployeeId] ||
              reporteeChain[user.employeeId] ||
              reporteeChain[user._id] ||
              {}
            const managerChain = chainObj.managerChain || ''
            const managerList = managerChain ? managerChain.split(' -> ') : []

            const employeeData = {} // Date-wise buckets
            userPoints.forEach((p) => {
              const d = dayjs(p.timestamp || p.createdAt).format('YYYY-MM-DD')
              if (!employeeData[d]) employeeData[d] = []
              employeeData[d].push(p)
            })

            // Calculate monthly totals for summary
            let totalDistance = 0
            let totalVisits = 0
            dates.forEach((d) => {
              const pts = employeeData[d] || []
              totalDistance += this.calculateTotalDistance(pts)
              const visits = new Set()
              pts.forEach((p) => {
                if (p.activity?.caseTaskDetails)
                  visits.add(p.activity.caseTaskDetails._id || p.activity.caseTaskDetails)
              })
              totalVisits += visits.size
            })

            dates.forEach((date, dateIdx) => {
              const pts = employeeData[date] || []
              const statusFromMap = attendanceMap[user._id]?.[date] || null
              const dayData = this.processDailyPoints(pts, statusFromMap)

              const constructRow = (pair = null, isFirst = true) => {
                const row = []
                structure.keys.forEach((key) => {
                  if (key.startsWith('Manager Level ')) {
                    const idx = parseInt(key.replace('Manager Level ', '')) - 1
                    row.push(dateIdx === 0 && isFirst ? managerList[idx] || '-' : '')
                    return
                  }

                  switch (key) {
                    case 'Employee Name':
                      row.push(
                        dateIdx === 0 && isFirst
                          ? user.fullName || `${user.firstName} ${user.lastName || ''}`
                          : ''
                      )
                      break
                    case 'Emp Id':
                      row.push(
                        dateIdx === 0 && isFirst ? user.orgEmployeeId || user.employeeId : ''
                      )
                      break
                    case 'Department':
                      row.push(
                        dateIdx === 0 && isFirst ? user.department?.departmentName || '-' : ''
                      )
                      break
                    case 'Designation':
                      row.push(
                        dateIdx === 0 && isFirst ? user.designation?.designationName || '-' : ''
                      )
                      break
                    case 'City':
                      row.push(
                        dateIdx === 0 && isFirst ? user.city || user.address?.city || '-' : ''
                      )
                      break
                    case 'Direct Manager':
                      row.push(dateIdx === 0 && isFirst ? user.reportesTo?.fullName || '-' : '')
                      break
                    case 'Manager Hierarchy':
                      row.push(dateIdx === 0 && isFirst ? managerChain : '')
                      break
                    case 'Date':
                      row.push(isFirst ? dayjs(date).format('DD/MM/YYYY') : '')
                      break
                    case 'Attendance':
                      row.push(isFirst ? dayData.attendanceStatus : '')
                      break
                    case 'First Clock-In':
                      row.push(isFirst ? dayData.firstClockIn : '')
                      break
                    case 'Last Clock-Out':
                      row.push(isFirst ? dayData.lastClockOut : '')
                      break
                    case 'Shift Start Time':
                      row.push(
                        isFirst
                          ? this.attendanceSettings?.shiftStartTime
                            ? dayjs(this.attendanceSettings.shiftStartTime, 'HH:mm').format(
                                'hh:mm A'
                              )
                            : '-'
                          : ''
                      )
                      break
                    case 'Planned Working Hour':
                      row.push(
                        isFirst
                          ? this.attendanceSettings?.workingHours
                            ? `${this.attendanceSettings.workingHours} hrs`
                            : '-'
                          : ''
                      )
                      break
                    case 'Completed Working Hour':
                      row.push(isFirst ? dayData.workingHours : '')
                      break
                    case 'Breaks Count':
                      row.push(isFirst ? dayData.breaksCount : '')
                      break
                    case 'Total Distance Per Day':
                      row.push(isFirst ? dayData.dailyDistance.toFixed(2) : '')
                      break
                    case 'Daily Total Visits':
                      row.push(isFirst ? dayData.dailyVisitCount : '')
                      break
                    case 'Month':
                      row.push(dateIdx === 0 && isFirst ? dayjs(date).format('MMMM YYYY') : '')
                      break
                    case 'Total Distance Traveled':
                      row.push(dateIdx === 0 && isFirst ? totalDistance.toFixed(2) : '')
                      break
                    case 'Total Visits Done':
                      row.push(dateIdx === 0 && isFirst ? totalVisits : '')
                      break

                    // Pair columns
                    case 'Activity Date':
                      row.push(pair ? dayjs(pair.originArrivalTime).format('DD/MM/YYYY') : '-')
                      break
                    case 'Activity':
                      row.push(pair?.originAction || '-')
                      break
                    case 'Activity Start Time':
                    case 'Arrival Time':
                      row.push(
                        pair?.originArrivalTime
                          ? dayjs(pair.originArrivalTime).format('hh:mm A')
                          : '-'
                      )
                      break
                    case 'Activity End Time':
                    case 'Departure Time':
                      row.push(
                        pair?.originDepartureTime
                          ? dayjs(pair.originDepartureTime).format('hh:mm A')
                          : '-'
                      )
                      break
                    case 'Duration of Activity':
                    case 'Duration of Visit':
                      row.push(pair?.originDuration || '-')
                      break
                    case 'Activity Start Location':
                    case 'Visit Location':
                      row.push(pair?.origin || '-')
                      break
                    case 'Activity End Location':
                      row.push(pair?.destination || '-')
                      break
                    case 'Customer Name':
                      row.push(pair?.customerName || '-')
                      break
                    case 'Distance Breakup Per Day':
                      row.push(
                        pair?.segmentDistance !== undefined ? pair.segmentDistance.toFixed(2) : '-'
                      )
                      break
                    default:
                      row.push('-')
                      break
                  }
                })
                return row
              }

              if (dayData.pairs && dayData.pairs.length > 0) {
                dayData.pairs.forEach((p, pIdx) => worksheet.addRow(constructRow(p, pIdx === 0)))
              } else {
                worksheet.addRow(constructRow(null, true))
              }
            })
            srNo++
          }

          worksheet.columns.forEach((col) => {
            col.width = 25
          })
          const buffer = await workbook.xlsx.writeBuffer()
          this.downloadBlob(buffer, `Muster_Report_II_${start}_${end}.xlsx`)
          this.$toast?.success('Report generated successfully!')
        }
      } catch (error) {
        console.error('Attendance export error:', error)
        this.$toast?.error('Failed to generate report. Please try again.')
      }
    },

    async fetchAttendanceInChunks(users, start, end) {
      const results = []
      const batchSize = 5 // max concurrent requests
      const usersPerChunk = 20
      const userIds = users.map((u) => u._id)

      const chunkedUsers = []
      for (let i = 0; i < userIds.length; i += usersPerChunk) {
        chunkedUsers.push(userIds.slice(i, i + usersPerChunk).join(','))
      }

      const paramsArray = []
      chunkedUsers.forEach((uStr) => {
        paramsArray.push({
          start,
          end,
          userIds: uStr,
          limit: 100000
        })
      })

      for (let i = 0; i < paramsArray.length; i += batchSize) {
        const batch = paramsArray.slice(i, i + batchSize)
        const requests = batch.map((p) => this.$axios.get('/user/v2/attendance', { params: p }))
        const responses = await Promise.allSettled(requests)

        responses.forEach((res) => {
          if (res.status === 'fulfilled' && res.value?.data) {
            const dataArr =
              res.value.data.employeeAttendance ||
              res.value.data.data?.data ||
              res.value.data.data ||
              []
            if (Array.isArray(dataArr)) {
              results.push(...dataArr)
            }
          }
        })
      }
      return results
    },

    async fetchTimeSeriesConcurrently(users, start, end) {
      const resultsMap = {}
      const concurrencyLimit = 10 // Max concurrent requests to prevent server overload

      for (let i = 0; i < users.length; i += concurrencyLimit) {
        const batchUsers = users.slice(i, i + concurrencyLimit)

        const requests = batchUsers.map(async (user) => {
          try {
            const tsResp = await this.$axios.get(`/location-time-series/${user._id}`, {
              params: { start, end, limit: 100000 }
            })
            return {
              userId: user._id,
              data: tsResp.data?.data?.data || []
            }
          } catch (err) {
            console.error(`Failed to fetch TS for ${user._id}`, err)
            return { userId: user._id, data: [] }
          }
        })

        const batchResults = await Promise.all(requests)
        batchResults.forEach((res) => {
          resultsMap[res.userId] = res.data
        })
      }
      return resultsMap
    },

    async generateAttendanceMatrix(items, currentMonthDates, selectedColumns) {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Attendance')
      const sortedItems = sortDropdownOptions([...items], 'name')
      if (!this.dates || this.dates.length < 2) {
        this.$toast.error('Please select a date range first')
        return
      }
      const sortedDates = [...this.dates].sort()
      const start = sortedDates[0]
      const end = sortedDates[1]

      // Fetch manager chain local logic (or from storage)
      const reporteeChain = this.$storage?.getUniversal('userReporteeChain') || {}
      let maxManagers = 0
      sortedItems.forEach((employee) => {
        const chain =
          reporteeChain[employee.orgEmployeeId || employee.employeeId]?.managerChain || ''
        const list = chain ? chain.split(' -> ') : []
        maxManagers = Math.max(maxManagers, list.length)
      })
      const managerHeaders = Array.from({ length: maxManagers }, (_, i) => `Manager Level ${i + 1}`)

      // Header Row 1 (Main)
      const mainHeaderRow = worksheet.getRow(1)
      const subHeaderRow = worksheet.getRow(2)
      let colIndex = 1

      const staticHeaders = [
        'Name',
        'Employee Id',
        'Role',
        'Designation',
        'City',
        ...managerHeaders,
        'Manager Hierarchy'
      ]
      staticHeaders.forEach((header) => {
        const cell = mainHeaderRow.getCell(colIndex)
        cell.value = header
        cell.font = { bold: true }
        worksheet.mergeCells(1, colIndex, 2, colIndex)
        colIndex++
      })

      currentMonthDates.forEach((date) => {
        const startCol = colIndex
        const dateCols = selectedColumns.filter((c) => ATTENDANCE_VALID_COLS.includes(c))

        if (dateCols.length > 0) {
          worksheet.mergeCells(1, startCol, 1, startCol + dateCols.length - 1)
          mainHeaderRow.getCell(startCol).value = date
          mainHeaderRow.getCell(startCol).font = { bold: true }
          mainHeaderRow.getCell(startCol).alignment = { horizontal: 'center' }

          dateCols.forEach((col) => {
            subHeaderRow.getCell(colIndex).value = col
            subHeaderRow.getCell(colIndex).font = { bold: true }
            colIndex++
          })
        }
      })

      const summaryHeaders = [
        'Total Absent',
        'Total Present',
        'Total Week-off',
        'Total Miss punch',
        'Total Leaves',
        'Clock In (in %)'
      ]
      summaryHeaders.forEach((header) => {
        const cell = mainHeaderRow.getCell(colIndex)
        cell.value = header
        cell.font = { bold: true }
        worksheet.mergeCells(1, colIndex, 2, colIndex)
        colIndex++
      })

      mainHeaderRow.height = 30
      subHeaderRow.height = 25

      sortedItems.forEach((item) => {
        const chain = reporteeChain[item.orgEmployeeId || item.employeeId]?.managerChain || ''
        const list = chain ? chain.split(' -> ') : []
        const managerCols = Array.from({ length: maxManagers }, (_, i) => list[i] || '-')

        const row = [
          item.name,
          item.orgEmployeeId || item.employeeId,
          item.role,
          item.designation,
          item.city,
          ...managerCols,
          chain
        ]

        let totalAbsent = 0
        let totalPresent = 0
        let totalWeekOff = 0
        let totalMissPunch = 0
        let totalLeaves = 0
        let daysCount = 0

        currentMonthDates.forEach((date) => {
          daysCount++
          const att = item.attendance && item.attendance[date] ? item.attendance[date] : {}

          const dateCols = selectedColumns.filter((c) => ATTENDANCE_VALID_COLS.includes(c))

          dateCols.forEach((col) => {
            const mapper = ATTENDANCE_COLUMN_MAPPERS[col]
            const val = mapper ? mapper(att, date) : att[col] || '-'
            row.push(val)

            if (col === 'Attendance') {
              if (val === 'ABSENT' || val === 'A') totalAbsent++
              if (val === 'PRESENT' || val === 'P') totalPresent++
              if (val === 'WEEK_OFF' || val === 'WO') totalWeekOff++
              if (val === 'LEAVE' || val === 'L') totalLeaves++
              if (val === 'MISS_PUNCH' || val === 'MP') totalMissPunch++
            }
          })
        })

        const clockInPercentage =
          daysCount > 0 ? `${((totalPresent * 100) / daysCount).toFixed(2)} %` : '0.00 %'
        row.push(
          totalAbsent,
          totalPresent,
          totalWeekOff,
          totalMissPunch,
          totalLeaves,
          clockInPercentage
        )
        worksheet.addRow(row)
      })

      const buffer = await workbook.xlsx.writeBuffer()
      this.downloadBlob(buffer, `Attendance_${start}_${end}.xlsx`)
      this.$toast?.success('Report generated successfully!')
    },

    async generateMusterReportI(items, currentMonthDates, selectedColumns) {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Muster Report-I')
      const sortedItems = sortDropdownOptions([...items], 'name')
      if (!this.dates || this.dates.length < 2) {
        this.$toast.error('Please select a date range first')
        return
      }
      const sortedDates = [...this.dates].sort()
      const start = sortedDates[0]
      const end = sortedDates[1]

      const reporteeChain = this.$storage?.getUniversal('userReporteeChain') || {}
      let maxManagers = 0
      sortedItems.forEach((employee) => {
        const chain =
          reporteeChain[employee.orgEmployeeId || employee.employeeId]?.managerChain || ''
        const list = chain ? chain.split(' -> ') : []
        maxManagers = Math.max(maxManagers, list.length)
      })
      const managerHeaders = Array.from({ length: maxManagers }, (_, i) => `Manager Level ${i + 1}`)

      const headers = [
        'Sr.No',
        'Employee Name',
        'Employee Id',
        'Role',
        'Designation',
        'City',
        ...managerHeaders,
        'Manager Hierarchy',
        'Date',
        ...selectedColumns,
        'Monthly Distance (km)'
      ]

      const headerRow = worksheet.getRow(1)
      headerRow.values = headers
      headerRow.font = { bold: true }

      let srNo = 1
      sortedItems.forEach((employee) => {
        let totalDistance = 0
        const chain =
          reporteeChain[employee.orgEmployeeId || employee.employeeId]?.managerChain || ''
        const list = chain ? chain.split(' -> ') : []
        const managerCols = Array.from({ length: maxManagers }, (_, i) => list[i] || '-')

        currentMonthDates.forEach((date, dateIdx) => {
          const att =
            employee.attendance && employee.attendance[date] ? employee.attendance[date] : {}
          if (att['Distance (km)'] && !isNaN(parseFloat(att['Distance (km)']))) {
            totalDistance += parseFloat(att['Distance (km)'])
          }

          const row = [
            dateIdx === 0 ? srNo : '',
            dateIdx === 0 ? employee.name : '',
            dateIdx === 0 ? employee.orgEmployeeId || employee.employeeId : '',
            dateIdx === 0 ? employee.role : '',
            dateIdx === 0 ? employee.designation : '',
            dateIdx === 0 ? employee.city : '',
            ...(dateIdx === 0 ? managerCols : Array(maxManagers).fill('')),
            dateIdx === 0 ? chain : '',
            dayjs(date).format('DD/MM/YYYY'),
            ...selectedColumns.map((col) => {
              const mapper = ATTENDANCE_COLUMN_MAPPERS[col]
              return mapper ? mapper(att, date) : att[col] || '-'
            }),
            dateIdx === 0 ? '---' : '' // Placeholder for Monthly Distance
          ]
          const addedRow = worksheet.addRow(row)
          // Update total distance in the first row after processing all dates
          if (dateIdx === currentMonthDates.length - 1) {
            const firstRowIdx = addedRow.number - currentMonthDates.length + 1
            worksheet.getRow(firstRowIdx).getCell(headers.length).value = totalDistance.toFixed(2)
          }
        })
        srNo++
      })

      const buffer = await workbook.xlsx.writeBuffer()
      this.downloadBlob(buffer, `Muster_Report_I_${start}_${end}.xlsx`)
      this.$toast?.success('Report generated successfully!')
    },

    getTimeSeriesHeaders(reportTitle, selectedColumns) {
      const reporteeChain = this.$storage?.getUniversal('userReporteeChain') || {}
      let maxManagers = localStorage.getItem('maxManagerCount') || 3 // rough fallback
      const managerHeaders = Array.from({ length: maxManagers }, (_, i) => `Manager Level ${i + 1}`)

      const baseHeaders = [
        'Sr.No',
        'Employee Name',
        'Employee Id',
        'Role',
        'Designation',
        'City',
        ...managerHeaders,
        'Manager Hierarchy'
      ]
      return [...baseHeaders, ...selectedColumns]
    },

    processDailyPoints(points, statusFromApi) {
      if (!points || points.length === 0) {
        return {
          attendanceStatus: statusFromApi || '-',
          firstClockIn: '-',
          lastClockOut: '-',
          dailyDistance: 0,
          workingHours: '-',
          breaksCount: 0,
          dailyVisitCount: 0,
          pairs: []
        }
      }

      points.sort(
        (a, b) => new Date(a.timestamp || a.createdAt) - new Date(b.timestamp || b.createdAt)
      )

      const firstPoint = points[0]
      const lastPoint = points.length > 1 ? points[points.length - 1] : firstPoint
      const firstTime = dayjs(firstPoint.timestamp || firstPoint.createdAt)
      const lastTime = dayjs(lastPoint.timestamp || lastPoint.createdAt)

      const totalDist = this.calculateTotalDistance(points)
      const stops = this.groupPointsIntoStops(points)
      const pairs = []

      if (stops.length > 0) {
        for (let i = 0; i < stops.length; i++) {
          const sCurr = stops[i]
          const sPrev = i > 0 ? stops[i - 1] : null
          const sNext = i < stops.length - 1 ? stops[i + 1] : null

          let distFromPrev = 0
          if (sPrev)
            distFromPrev = this.calculateDistance(sPrev.lat, sPrev.lng, sCurr.lat, sCurr.lng)

          const durationMs = dayjs(sCurr.endTime).diff(dayjs(sCurr.startTime))

          pairs.push({
            origin: sCurr.address,
            destination: sNext ? sNext.address : '-',
            originAction: sCurr.action,
            segmentDistance: distFromPrev,
            customerName: sCurr.customerName || '-',
            originArrivalTime: sCurr.startTime,
            originDepartureTime: sCurr.endTime,
            originDuration: this.formatDuration(durationMs)
          })
        }
      } else {
        const distFirstLast = this.calculateDistance(
          firstPoint.lat || firstPoint.location?.coordinates?.[1] || 0,
          firstPoint.lng || firstPoint.location?.coordinates?.[0] || 0,
          lastPoint.lat || lastPoint.location?.coordinates?.[1] || 0,
          lastPoint.lng || lastPoint.location?.coordinates?.[0] || 0
        )
        if (distFirstLast > 0.1) {
          pairs.push({
            origin: firstPoint.address || firstPoint.location?.address || '-',
            destination: lastPoint.address || lastPoint.location?.address || '-',
            originAction: '-',
            segmentDistance: 0,
            customerName: '-',
            originArrivalTime: firstPoint.timestamp || firstPoint.createdAt,
            originDepartureTime: null,
            originDuration: '-'
          })
          pairs.push({
            origin: lastPoint.address || lastPoint.location?.address || '-',
            destination: '-',
            originAction: '-',
            segmentDistance: distFirstLast,
            customerName: '-',
            originArrivalTime: lastPoint.timestamp || lastPoint.createdAt,
            originDepartureTime: null,
            originDuration: '-'
          })
        } else {
          pairs.push({
            origin: firstPoint.address || firstPoint.location?.address || '-',
            destination: '-',
            originAction: '-',
            segmentDistance: totalDist,
            customerName: '-',
            originArrivalTime: null,
            originDepartureTime: null,
            originDuration: '-'
          })
        }
      }

      const workingHours = lastTime.diff(firstTime, 'hour', true)

      return {
        attendanceStatus: statusFromApi || '-',
        firstClockIn: firstTime.format('hh:mm A'),
        lastClockOut: points.length > 1 ? lastTime.format('hh:mm A') : '-',
        dailyDistance: totalDist,
        workingHours: workingHours > 0 ? workingHours.toFixed(2) + ' hrs' : '-',
        breaksCount: 0, // Placeholder
        dailyVisitCount: this.calculateDailyUniqueVisits(points),
        pairs: pairs
      }
    },

    groupPointsIntoStops(points) {
      const groups = []
      let currentGroup = null

      points.forEach((p) => {
        const state = p.state || 'UNKNOWN'
        const isStill = state === 'STILL'
        const pAddress = (p.address || p.location?.address || '').trim()
        const pPrefix = pAddress.split(',')[0].trim()
        const pTime = p.timestamp || p.createdAt

        let lat, lng
        if (p.location?.coordinates) {
          lng = p.location.coordinates[0]
          lat = p.location.coordinates[1]
        } else {
          lat = p.lat
          lng = p.lng
        }
        if (!lat || !lng) return

        if (isStill) {
          if (currentGroup && currentGroup.addressPrefix === pPrefix) {
            currentGroup.endTime = pTime
          } else {
            if (currentGroup) groups.push(currentGroup)
            currentGroup = {
              address: pAddress,
              addressPrefix: pPrefix,
              lat,
              lng,
              startTime: pTime,
              endTime: pTime,
              action: p.activity?.action || '-',
              customerName: p.activity?.caseTaskDetails?.customerName || '-'
            }
          }
        } else if (currentGroup) {
          groups.push(currentGroup)
          currentGroup = null
        }
      })
      if (currentGroup) groups.push(currentGroup)
      return groups
    },

    calculateDistance(lat1, lon1, lat2, lon2) {
      if (!lat1 || !lon1 || !lat2 || !lon2) return 0
      const R = 6371
      const dLat = this.deg2rad(lat2 - lat1)
      const dLon = this.deg2rad(lon2 - lon1)
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.deg2rad(lat1)) *
          Math.cos(this.deg2rad(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    },

    deg2rad(deg) {
      return deg * (Math.PI / 180)
    },

    formatDuration(ms) {
      if (!ms || ms < 0) ms = 0
      const seconds = Math.floor(ms / 1000)
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      if (h > 0) return `${h}h ${m}m ${s}s`
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    },

    calculateTotalDistance(points) {
      let total = 0
      for (let i = 0; i < points.length - 1; i++) {
        let lat1, lng1, lat2, lng2
        const p1 = points[i],
          p2 = points[i + 1]
        if (p1.location?.coordinates) {
          lng1 = p1.location.coordinates[0]
          lat1 = p1.location.coordinates[1]
        } else {
          lat1 = p1.lat
          lng1 = p1.lng
        }
        if (p2.location?.coordinates) {
          lng2 = p2.location.coordinates[0]
          lat2 = p2.location.coordinates[1]
        } else {
          lat2 = p2.lat
          lng2 = p2.lng
        }
        if (lat1 && lng1 && lat2 && lng2) total += this.calculateDistance(lat1, lng1, lat2, lng2)
      }
      return total
    },

    calculateDailyUniqueVisits(points) {
      if (!points) return 0
      const unique = new Set()
      points.forEach((p) => {
        if (p.activity?.caseTaskDetails)
          unique.add(p.activity.caseTaskDetails._id || p.activity.caseTaskDetails)
      })
      return unique.size
    },

    downloadBlob(buffer, fileName) {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
.v-card {
  border: 1px solid #b3b8c0;
}
.v-btn {
  text-transform: none;
  font-weight: 500;
}

.v-dialog > .v-card > .v-card__text {
  padding: 0;
}

/* Premium Filter Dialog Styling */
.report-gen-card {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}

.date-picker-trigger {
  min-height: 56px;
  background-color: #fafafa;
}

.report-card-container {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.report-card-container:hover {
  transform: translateY(-8px);
}

.report-card-container:hover .v-card {
  box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1) !important;
  border-color: #1a73e8 !important;
}

/* Custom checkbox styling */
.v-input--checkbox {
  margin-top: 0;
  padding-top: 0;
}

.v-input--checkbox .v-input__slot {
  margin-bottom: 0;
}
.form-title {
  font-size: 1.25rem;
  padding: 0px !important;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Report Preview Table Styling */
.report-preview-table ::v-deep th {
  padding: 16px 24px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
  background-color: #f8fafc !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.75rem;
  white-space: nowrap;
}

.report-preview-table ::v-deep td {
  padding: 16px 24px !important;
  color: #4b5563 !important;
  font-size: 0.875rem;
  border-bottom: 1px solid #f1f5f9 !important;
  white-space: nowrap;
}

.report-preview-table ::v-deep tr:hover {
  background-color: #f1f5f9 !important;
}
</style>
